import React, { useEffect, useRef, useState } from "react";
import { api, fmt } from "../lib/api";
import { toast } from "sonner";
import { MessageSquare, Send, X, Shield, Check } from "lucide-react";

export function AdminChatPanel() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const lastTsRef = useRef(null);

  const loadConvs = async () => {
    try {
      const r = await api.get("/chat/admin/conversations");
      setConversations(r.data);
    } catch {}
  };

  const loadMessages = async (userId, initial = true) => {
    try {
      const params = initial ? {} : (lastTsRef.current ? { since: lastTsRef.current } : {});
      const r = await api.get(`/chat/admin/messages/${userId}`, { params });
      if (initial) {
        setMessages(r.data);
      } else {
        setMessages(prev => {
          const seen = new Set(prev.map(m => m.message_id));
          return [...prev, ...r.data.filter(m => !seen.has(m.message_id))];
        });
      }
      if (r.data.length) lastTsRef.current = r.data[r.data.length - 1].ts;
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 50);
    } catch {}
  };

  useEffect(() => {
    loadConvs();
    const iv = setInterval(() => {
      loadConvs();
      if (selected) loadMessages(selected.user_id, false);
    }, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const openConv = async (c) => {
    setSelected(c);
    lastTsRef.current = null;
    setMessages([]);
    await loadMessages(c.user_id, true);
  };

  const send = async () => {
    if (!selected || !reply.trim() || busy) return;
    setBusy(true);
    try {
      const r = await api.post("/chat/admin/reply", { user_id: selected.user_id, text: reply.trim() });
      setMessages(prev => [...prev, r.data]);
      lastTsRef.current = r.data.ts;
      setReply("");
      setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 50);
      loadConvs();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const closeConv = async (userId) => {
    try {
      await api.post("/chat/admin/close", { user_id: userId });
      toast.success("Conversation closed");
      loadConvs();
    } catch {}
  };

  return (
    <div className="grid md:grid-cols-[320px_1fr] gap-4 min-h-[500px]">
      {/* Conversation list */}
      <div className="lux-card overflow-hidden">
        <div className="p-3 border-b border-white/10 flex items-center gap-2">
          <MessageSquare size={16} className="text-[#d4af37]"/>
          <span className="font-serif text-sm">Conversations ({conversations.length})</span>
        </div>
        <div className="max-h-[500px] overflow-y-auto">
          {conversations.length === 0 && <div className="p-6 text-center text-white/40 text-xs">No conversations yet</div>}
          {conversations.map(c => (
            <button key={c.user_id} data-testid={`admin-conv-${c.user_id}`}
                    onClick={()=>openConv(c)}
                    className={`w-full text-left p-3 border-t border-white/5 hover:bg-white/5 transition ${selected?.user_id === c.user_id ? "bg-white/5" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-medium text-sm truncate">{c.user_name || c.user_email}</div>
                {c.unread_by_admin > 0 && (
                  <span className="bg-red-600 text-white text-[9px] font-bold rounded-full px-1.5 py-0.5">{c.unread_by_admin}</span>
                )}
              </div>
              <div className="text-[10px] text-white/40 truncate mt-0.5">{c.last_message || "—"}</div>
              <div className="text-[9px] text-white/30 mt-0.5">{c.last_ts ? new Date(c.last_ts).toLocaleString() : ""}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Chat panel */}
      <div className="lux-card flex flex-col overflow-hidden">
        {selected ? (
          <>
            <div className="p-3 border-b border-white/10 flex items-center justify-between">
              <div>
                <div className="font-medium text-sm">{selected.user_name || selected.user_email}</div>
                <div className="text-[10px] text-white/40">{selected.user_email}</div>
              </div>
              <button data-testid="admin-conv-close" onClick={()=>closeConv(selected.user_id)}
                      className="btn-ghost px-3 py-1 rounded text-[10px] flex items-center gap-1">
                <X size={12}/>Close
              </button>
            </div>
            <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[300px]">
              {messages.length === 0 && <div className="text-center text-white/40 text-xs py-10">No messages yet</div>}
              {messages.map(m => (
                <div key={m.message_id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-lg px-3 py-2 text-sm ${m.sender === "admin" ? "bg-[#d4af37] text-black" : "bg-[#1a1a1a] border border-white/10"}`}>
                    <div className="whitespace-pre-wrap">{m.text}</div>
                    <div className={`text-[9px] mt-1 ${m.sender === "admin" ? "text-black/50" : "text-white/40"}`}>
                      {new Date(m.ts).toLocaleTimeString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 p-2 flex gap-2">
              <input data-testid="admin-reply-input" value={reply} onChange={e=>setReply(e.target.value)}
                     onKeyDown={e=>{ if (e.key === "Enter") send(); }}
                     placeholder="Reply to user..."
                     className="flex-1 bg-[#1a1a1a] border border-white/10 rounded px-3 py-2 text-sm focus:border-[#d4af37] focus:outline-none"/>
              <button data-testid="admin-reply-send" onClick={send} disabled={busy || !reply.trim()}
                      className="btn-gold px-3 rounded"><Send size={16}/></button>
            </div>
          </>
        ) : (
          <div className="flex items-center justify-center h-full text-white/30 text-sm">Select a conversation</div>
        )}
      </div>
    </div>
  );
}


export function AdminKycPanel() {
  const [items, setItems] = useState([]);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    try {
      const r = await api.get("/compliance/admin/kyc/list", { params: filter ? { status: filter } : {} });
      setItems(r.data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openDetail = async (id) => {
    try {
      const r = await api.get(`/compliance/admin/kyc/${id}`);
      setDetail(r.data);
    } catch {}
  };

  const review = async (decision) => {
    const notes = window.prompt(`${decision === "approved" ? "Approval" : "Rejection"} notes (optional):`, "");
    try {
      await api.post("/compliance/admin/kyc/review", { submission_id: detail.submission_id, decision, notes });
      toast.success(`KYC ${decision}`);
      setDetail(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending","approved","rejected","",].map(f=>(
          <button key={f||"all"} data-testid={`kyc-filter-${f||"all"}`}
                  onClick={()=>setFilter(f)}
                  className={`px-3 py-1.5 rounded text-xs uppercase ${filter===f?"btn-gold":"btn-ghost"}`}>
            {f || "All"}
          </button>
        ))}
      </div>
      <div className="lux-card overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-black/40 text-white/50 text-xs uppercase">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Doc</th>
              <th className="text-left p-3">Country</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Submitted</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-white/40">No submissions</td></tr>}
            {items.map(k=>(
              <tr key={k.submission_id} className="border-t border-white/5" data-testid={`kyc-row-${k.submission_id}`}>
                <td className="p-3">{k.user_email}</td>
                <td className="p-3">{k.full_name}</td>
                <td className="p-3">{k.doc_type} <span className="text-white/40">{k.doc_number}</span></td>
                <td className="p-3">{k.country}</td>
                <td className="p-3"><span className={`text-xs uppercase ${k.status==="approved"?"text-emerald-400":k.status==="pending"?"text-yellow-400":"text-red-400"}`}>{k.status}</span></td>
                <td className="p-3 text-xs text-white/50">{new Date(k.submitted_at).toLocaleString()}</td>
                <td className="p-3 text-right"><button onClick={()=>openDetail(k.submission_id)} className="btn-ghost px-3 py-1 rounded text-xs">Review</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={()=>setDetail(null)}>
          <div className="lux-card p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e=>e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-xl flex items-center gap-2"><Shield/>{detail.full_name}</h3>
              <button onClick={()=>setDetail(null)} className="text-white/40 hover:text-white"><X/></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3 text-sm mb-4">
              <div><span className="text-white/40">Type:</span> {detail.doc_type}</div>
              <div><span className="text-white/40">Number:</span> {detail.doc_number}</div>
              <div><span className="text-white/40">DOB:</span> {detail.dob}</div>
              <div><span className="text-white/40">Country:</span> {detail.country}</div>
              <div className="md:col-span-2"><span className="text-white/40">Address:</span> {detail.address}</div>
            </div>
            <div className="grid md:grid-cols-3 gap-3 mb-4">
              {["front_image","back_image","selfie_image"].map(k=>(
                detail[k] && (
                  <div key={k}>
                    <div className="text-[10px] uppercase text-white/40 mb-1">{k.replace("_"," ")}</div>
                    <img src={detail[k]} alt={k} className="rounded border border-white/10"/>
                  </div>
                )
              ))}
            </div>
            {detail.status === "pending" && (
              <div className="flex gap-2 justify-end">
                <button data-testid="kyc-reject" onClick={()=>review("rejected")} className="btn-burgundy px-5 py-2 rounded text-sm">Reject</button>
                <button data-testid="kyc-approve" onClick={()=>review("approved")} className="btn-gold px-5 py-2 rounded text-sm flex items-center gap-1"><Check size={14}/>Approve</button>
              </div>
            )}
            {detail.status !== "pending" && (
              <div className="text-sm text-white/60">Reviewed by {detail.reviewed_by} on {new Date(detail.reviewed_at).toLocaleString()} — {detail.notes || "(no notes)"}</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}


export function AdminCashoutPanel() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("pending");

  const load = async () => {
    try {
      const r = await api.get("/compliance/admin/cashout/list", { params: filter ? { status: filter } : {} });
      setItems(r.data);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const action = async (req, act) => {
    const notes = window.prompt(`${act} notes:`, "");
    try {
      await api.post("/compliance/admin/cashout/action", { request_id: req.request_id, action: act, notes });
      toast.success(`Request ${act}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending","approved","rejected",""].map(f=>(
          <button key={f||"all"} onClick={()=>setFilter(f)}
                  className={`px-3 py-1.5 rounded text-xs uppercase ${filter===f?"btn-gold":"btn-ghost"}`}>
            {f || "All"}
          </button>
        ))}
      </div>
      <div className="lux-card overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-black/40 text-white/50 text-xs uppercase">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-right p-3">Amount</th>
              <th className="text-left p-3">Method</th>
              <th className="text-left p-3">Details</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Created</th>
              <th className="text-right p-3"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-white/40">No requests</td></tr>}
            {items.map(r=>(
              <tr key={r.request_id} className="border-t border-white/5">
                <td className="p-3">{r.user_email}</td>
                <td className="p-3 text-right text-[#d4af37]">{fmt(r.amount, r.currency)}</td>
                <td className="p-3">{r.method}</td>
                <td className="p-3 text-xs">{r.account_details}</td>
                <td className="p-3"><span className={`text-xs uppercase ${r.status==="approved"?"text-emerald-400":r.status==="pending"?"text-yellow-400":"text-red-400"}`}>{r.status}</span></td>
                <td className="p-3 text-xs text-white/50">{new Date(r.created_at).toLocaleString()}</td>
                <td className="p-3 text-right">
                  {r.status === "pending" && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={()=>action(r,"approve")} className="btn-gold px-2 py-1 rounded text-[10px]">Approve</button>
                      <button onClick={()=>action(r,"reject")} className="btn-burgundy px-2 py-1 rounded text-[10px]">Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
